import { customSessionClient, inferAdditionalFields } from "better-auth/client/plugins";
import { createAuthClient } from "better-auth/react";

import {
  USER_REGISTRATION_CONTEXT,
  type UserRoleType,
  type BetterAuthConfig,
  type BetterAuthSession,
  type BetterAuthUser,
} from "@repo/shared";
import { getDeviceInfo } from "~/utils/deviceUtils";

type SignUpWithAdditionalFields = {
  email: string;
  password: string;
  name?: string;
  firstName: string;
  lastName: string;
  role?: UserRoleType;
  marketingConsent?: boolean;
  username?: string;
  phoneNumber?: string;
  callbackURL?: string;
  registrationContext?: string;
};

type SignUpWithDiscountCode = Omit<
  SignUpWithAdditionalFields,
  "name" | "firstName" | "lastName"
> & {
  discountCode: string;
};

const getBaseUrl = () => {
  if (import.meta.env.VITE_SOCIAL === "true") {
    return `${import.meta.env.VITE_TUNNEL_URL}/api/better-auth`;
  }

  if (typeof window === "undefined") return;

  return `${window.location.origin}/api/better-auth`;
};

export const authClient = createAuthClient({
  baseURL: getBaseUrl(),
  plugins: [customSessionClient<BetterAuthConfig>(), inferAdditionalFields<BetterAuthConfig>()],
  fetchOptions: {
    onRequest: async (context) => {
      if (typeof window !== "undefined") {
        const deviceInfo = getDeviceInfo();

        document.cookie = `device_id=${deviceInfo.deviceId}; path=/; SameSite=Lax`;
        document.cookie = `device_type=${encodeURIComponent(deviceInfo.deviceType)}; path=/; SameSite=Lax`;
        document.cookie = `device_os=${encodeURIComponent(deviceInfo.deviceOs)}; path=/; SameSite=Lax`;
        document.cookie = `device_os_version=${encodeURIComponent(deviceInfo.deviceOsVersion)}; path=/; SameSite=Lax`;
        document.cookie = `device_browser=${encodeURIComponent(deviceInfo.deviceBrowser)}; path=/; SameSite=Lax`;
        document.cookie = `device_browser_version=${encodeURIComponent(deviceInfo.deviceBrowserVersion)}; path=/; SameSite=Lax`;

        if (deviceInfo.deviceVendor) {
          document.cookie = `device_vendor=${encodeURIComponent(deviceInfo.deviceVendor)}; path=/; SameSite=Lax`;
        }
        if (deviceInfo.deviceModel) {
          document.cookie = `device_model=${encodeURIComponent(deviceInfo.deviceModel)}; path=/; SameSite=Lax`;
        }
      }
      return context;
    },
  },
});

export const { signIn, signUp, signOut, useSession, getSession } = authClient;

export type { BetterAuthSession, BetterAuthUser as BetterAuthSessionUser };

export const ensureBetterAuthSession = async () => {
  try {
    const sessionData = await getSession();

    if (!sessionData?.data?.user) {
      return { user: null, session: null };
    }

    return {
      user: sessionData.data.user as BetterAuthUser,
      session: sessionData.data as unknown as BetterAuthSession,
    };
  } catch (error) {
    console.error("Better Auth session error:", error);

    return { user: null, session: null, error };
  }
};

export const signInEmail = async (data: {
  email: string;
  password: string;
  rememberMe?: boolean;
}) => {
  try {
    const result = await signIn.email(data);

    if (result.error) {
      return { user: null, session: null, error: result.error };
    }

    return { user: (result.data?.user as BetterAuthUser) || null, session: null };
  } catch (error) {
    console.error("Better Auth sign in error:", error);

    return { user: null, session: null, error };
  }
};

export const signUpEmail = async (data: {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
}) => {
  try {
    const result = await signUp.email({
      email: data.email,
      password: data.password,
      name: `${data.firstName} ${data.lastName}`,
      firstName: data.firstName,
      lastName: data.lastName,
    } as any);

    if (result.error) {
      return { user: null, session: null, error: result.error };
    }

    return { user: (result.data?.user as BetterAuthUser) || null, session: null };
  } catch (error) {
    console.error("Better Auth sign up error:", error);

    return { user: null, session: null, error };
  }
};

export const signUpWithAdditionalFields = async (data: SignUpWithAdditionalFields) => {
  try {
    const result = await signUp.email({
      email: data.email,
      password: data.password,
      name: data.name || `${data.firstName} ${data.lastName}`,
      firstName: data.firstName,
      lastName: data.lastName,
      role: data.role,
      username: data.username,
      phoneNumber: data.phoneNumber,
      marketingConsent: data.marketingConsent || false,
      callbackURL: data.callbackURL,
      registrationContext: data.registrationContext || USER_REGISTRATION_CONTEXT.REGULAR,
    } as any);

    if (result.error) {
      return { user: null, session: null, error: result.error };
    }

    return { user: (result.data.user as BetterAuthUser) || null, session: null };
  } catch (error) {
    console.error("Better Auth sign up error:", error);

    return { user: null, session: null, error };
  }
};

export const signUpWithDiscountCode = async (data: SignUpWithDiscountCode) => {
  try {
    const result = await signUp.email({
      discountCode: data.discountCode,
      email: data.email,
      password: data.password,
      firstName: "Uczeń",
      lastName: data.email,
      role: data.role,
      username: data.username,
      marketingConsent: data.marketingConsent || false,
      callbackURL: data.callbackURL,
      registrationContext: data.registrationContext || USER_REGISTRATION_CONTEXT.REGULAR,
    } as any);

    if (result.error) {
      return { user: null, session: null, error: result.error };
    }

    return { user: (result.data.user as BetterAuthUser) || null, session: null };
  } catch (error) {
    console.error("Better Auth sign up error:", error);

    return { user: null, session: null, error };
  }
};

export const useAuthStatus = () => {
  const session = useSession();

  return {
    isLoggedIn: !!session.data?.user,
    user: session.data?.user ? session.data.user : null,
    isLoading: session.isPending,
    error: session.error,
  };
};

export const useCustomSession = () => {
  const session = useSession();

  return {
    ...session,
    data: session.data
      ? {
          ...session.data,
          user: session.data.user as BetterAuthUser,
        }
      : null,
  };
};

export const changePassword = async (data: {
  currentPassword: string;
  newPassword: string;
  revokeOtherSessions?: boolean;
}) => {
  try {
    const result = await authClient.changePassword({
      currentPassword: data.currentPassword,
      newPassword: data.newPassword,
      revokeOtherSessions: data.revokeOtherSessions ?? true,
    });

    if (result.error) {
      return { success: false, error: result.error };
    }

    return { success: true, data: result.data };
  } catch (error) {
    console.error("Better Auth change password error:", error);
    return { success: false, error };
  }
};

export const forgetPassword = async (email: string) => {
  try {
    const result = await authClient.forgetPassword({
      email,
      redirectTo: "/auth/reset-password",
    });

    if (result.error) {
      return { success: false, error: result.error };
    }

    return { success: true, data: result.data };
  } catch (error) {
    console.error("Better Auth forget password error:", error);
    return { success: false, error };
  }
};

export const resetPassword = async (data: { token: string; newPassword: string }) => {
  try {
    const result = await authClient.resetPassword({
      token: data.token,
      newPassword: data.newPassword,
    });

    if (result.error) {
      return { success: false, error: result.error };
    }

    return { success: true, data: result.data };
  } catch (error) {
    console.error("Better Auth reset password error:", error);
    return { success: false, error };
  }
};
